import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { colors } from './colors';

let theme = createTheme({
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    background: colors.background,
    text: colors.text,
    error: colors.error,
    warning: colors.warning,
    success: colors.success,
    info: colors.info,
    grey: colors.grey,
    action: {
      hover: 'rgba(199, 56, 102, 0.08)',
      selected: 'rgba(199, 56, 102, 0.12)',
      focus: 'rgba(199, 56, 102, 0.12)'
    }
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em'
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.01em'
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.005em'
    },
    h4: {
      fontWeight: 600
    },
    h5: {
      fontWeight: 500
    },
    h6: {
      fontWeight: 500
    },
    subtitle1: {
      fontWeight: 500
    },
    subtitle2: {
      fontWeight: 600
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.01em'
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5
    }
  },
  shape: {
    borderRadius: 12
  },
  shadows: [
    'none',
    colors.shadow.sm,
    colors.shadow.sm,
    colors.shadow.md,
    colors.shadow.md,
    colors.shadow.lg,
    colors.shadow.lg,
    colors.shadow.lg,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl,
    colors.shadow.xl
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: colors.background.gradient,
          backgroundAttachment: 'fixed',
          minHeight: '100vh'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 'none',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: colors.shadow.md
          }
        },
        contained: {
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
          boxShadow: colors.shadow.sm,
          color: '#FFFFFF',
          '&:hover': {
            boxShadow: colors.shadow.md,
            background: `linear-gradient(135deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
            transform: 'translateY(-2px)'
          },
          '&.MuiButton-containedPrimary': {
            color: '#FFFFFF',
            '&:hover': {
              color: '#FFFFFF'
            }
          },
          '&.MuiButton-containedSecondary': {
            background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.light} 100%)`,
            color: '#FFFFFF',
            '&:hover': {
              color: '#FFFFFF',
              background: `linear-gradient(135deg, ${colors.secondary.dark} 0%, ${colors.secondary.main} 100%)`
            }
          }
        },
        outlined: {
          borderColor: colors.border.light,
          color: colors.text.primary,
          borderWidth: 1.5,
          '&:hover': {
            borderColor: colors.primary.main,
            color: colors.primary.main,
            backgroundColor: 'rgba(199, 56, 102, 0.04)',
            transform: 'translateY(-2px)'
          }
        },
        text: {
          color: colors.text.primary,
          '&:hover': {
            backgroundColor: 'rgba(199, 56, 102, 0.08)',
            transform: 'translateY(-1px)'
          }
        }
      },
      variants: [
        {
          props: { variant: 'gradient' },
          style: {
            background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.light} 100%)`,
            color: '#FFFFFF',
            boxShadow: colors.shadow.sm,
            '&:hover': {
              boxShadow: colors.shadow.md,
              color: '#FFFFFF'
            }
          }
        },
        {
          props: { variant: 'soft' },
          style: {
            backgroundColor: 'rgba(199, 56, 102, 0.08)',
            color: colors.primary.main,
            '&:hover': {
              backgroundColor: 'rgba(199, 56, 102, 0.16)',
              transform: 'translateY(-2px)'
            }
          }
        }
      ]
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: colors.surface.elevated,
            transition: 'all 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.border.medium
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              borderWidth: 2
            }
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundImage: 'none',
          boxShadow: colors.shadow.sm,
          border: `1px solid ${colors.border.light}`
        },
        elevation1: {
          boxShadow: colors.shadow.sm
        },
        elevation2: {
          boxShadow: colors.shadow.md
        },
        elevation3: {
          boxShadow: colors.shadow.lg
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: colors.shadow.sm,
          border: `1px solid ${colors.border.light}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: colors.shadow.md,
            transform: 'translateY(-4px)'
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface.overlay,
          backdropFilter: 'blur(20px)',
          boxShadow: 'none',
          borderBottom: `1px solid ${colors.border.light}`
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          padding: '4px 0'
        },
        filled: {
          backgroundColor: 'rgba(199, 56, 102, 0.1)',
          color: colors.primary.main,
          '&:hover': {
            backgroundColor: 'rgba(199, 56, 102, 0.2)'
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: colors.shadow.xl
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: colors.shadow.lg
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(199, 56, 102, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(199, 56, 102, 0.16)'
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(199, 56, 102, 0.06)'
          }
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          minHeight: 48,
          '&.Mui-selected': {
            color: colors.primary.main
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: `2px solid ${colors.surface.elevated}`,
          boxShadow: colors.shadow.sm
        }
      }
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600,
          fontSize: '0.7rem'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12
        },
        standardSuccess: {
          backgroundColor: 'rgba(52, 199, 89, 0.1)',
          color: colors.success.dark
        },
        standardError: {
          backgroundColor: 'rgba(255, 59, 48, 0.1)',
          color: colors.error.dark
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 149, 0, 0.1)',
          color: colors.warning.dark
        },
        standardInfo: {
          backgroundColor: 'rgba(0, 122, 255, 0.1)',
          color: colors.info.dark
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.text.primary,
          borderRadius: 8,
          fontSize: '0.8rem',
          padding: '8px 12px'
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.border.light
        }
      }
    }
  }
});

theme = responsiveFontSizes(theme, { factor: 2.4 });

export default theme;